// Entry point. Wires up state subscription, renders screens, registers SW,
// and starts background services if a session is restored.

import { state, subscribe, notify } from './state.js';
import { restoreSession } from './services/session.js';
import { render } from './ui/screens.js';
import { requestGeolocation } from './services/geolocation.js';
import { acquireWakeLock } from './services/wakelock.js';
import { PWA_VERSION } from './version.js';
import { config } from './config.js';
import { queueLength } from './storage.js';
import { flush as flushQueue } from './services/queue.js';

console.info(`PlanillaPro Kiosk v${PWA_VERSION} — API: ${config.apiUrl}`);

state.pwaVersion = PWA_VERSION;

// Re-render on state changes.
subscribe(() => render());

// Initial render.
render();

// Restore session if any.
const restored = restoreSession();
state.view = restored ? 'keypad' : 'enroll';
// If already enrolled, remove any stale ?t= from the URL.
// If NOT enrolled, leave the URL alone so renderEnroll() can read the ?t= token.
if (restored && window.location.search) history.replaceState(null, '', '/');
notify();

// Capture PWA install prompt.
window.addEventListener('beforeinstallprompt', (e) => {
  e.preventDefault();
  state.installPrompt = e;
  notify();
});
window.addEventListener('appinstalled', () => {
  state.installPrompt = null;
  state.isInstalled = true;
  notify();
});

// Service worker.
if ('serviceWorker' in navigator) {
  const swVersion = window.__env?.PWA_VERSION || '0';
  navigator.serviceWorker.register(`/sw.js?v=${swVersion}`).catch((e) => console.debug('SW reg fail', e));
  navigator.serviceWorker.addEventListener('message', (evt) => {
    if (evt.data?.type === 'flush-marks') flushQueue();
  });
}

// Geolocation (informational; non-blocking).
requestGeolocation();

// Update pending queue count for header.
queueLength().then((n) => { state.pendingQueueCount = n; notify(); }).catch(() => {});

// If we have a session, fire up background services.
if (restored) {
  Promise.all([
    import('./services/heartbeat.js').then((m) => m.startHeartbeat()),
    import('./services/sync.js').then((m) => m.startSync()),
    import('./services/queue.js').then((m) => m.startQueueWorker())
  ]).catch((e) => console.debug('startup service error', e));
}

// First user interaction → fullscreen + wake lock (gesture-required APIs).
const onFirstGesture = async () => {
  document.removeEventListener('click', onFirstGesture);
  document.removeEventListener('touchstart', onFirstGesture);
  if (document.documentElement.requestFullscreen && !document.fullscreenElement) {
    try { await document.documentElement.requestFullscreen(); } catch { /* ignore — not allowed or already fullscreen */ }
  }
  acquireWakeLock();
  // Lock orientation after fullscreen is granted (Android Chrome).
  try { await screen.orientation?.lock?.('landscape'); } catch { /* ignore — desktop or not supported */ }
};
document.addEventListener('click', onFirstGesture, { once: false });
document.addEventListener('touchstart', onFirstGesture, { once: false });

// Background sync: best-effort registration.
if ('serviceWorker' in navigator && 'SyncManager' in window) {
  navigator.serviceWorker.ready.then((reg) => {
    try { reg.sync?.register('flush-marks'); } catch { /* ignore */ }
  });
}
